import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS_LIST = ["E", "E10+", "T", "M", "AO", "RP"];
const CHALLENGE_LIST = [
  { value: "eligible", label: "Eligible" },
  { value: "partly-eligible", label: "Partly Eligible" },
  { value: "not-eligible", label: "Not Eligible" },
];
const MULTIPLAYER_LIST = [
  { value: "none", label: "No Multiplayer" },
  { value: "full", label: "Full Multiplayer" },
  { value: "local_only", label: "Local Only" },
  { value: "limited", label: "Limited" },
];
const DELISTED_LIST = [
  { value: "none", label: "Available" },
  { value: "delisted", label: "Delisted" },
  { value: "servers-shutdown", label: "Servers Shut Down" },
  { value: "removed-digital", label: "Removed from Digital Store" },
  { value: "no-longer-playable", label: "No Longer Playable" },
  { value: "other", label: "Other" },
];

// Section wrapper with an "apply this field" checkbox. Kept outside the
// dialog component so React doesn't destroy inputs on every keystroke.
function BulkField({ name, label, enabled, onToggle, children }) {
  return (
    <div className="border border-white/5 rounded p-3 space-y-2">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox
          checked={!!enabled}
          onCheckedChange={onToggle}
          data-testid={`bulk-edit-toggle-${name}`}
        />
        <span className="text-sm font-medium text-white">{label}</span>
      </label>
      {enabled && <div className="pl-6">{children}</div>}
    </div>
  );
}

/**
 * Bulk-edit dialog for master games. Each section has an "Apply this field"
 * toggle — only the checked sections are sent to the backend, so admins can
 * leave everything else untouched. Mirrors the set-exact semantics of the
 * other bulk actions.
 */
export function BulkEditDialog({
  open,
  onOpenChange,
  selectedIds,
  onDone,
  genresList = [],
}) {
  const [running, setRunning] = useState(false);
  // `enabled` = which fields the admin has opted into. `values` = payload.
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({
    publisher: "",
    developer: "",
    releaseDate: "",
    genres: [],
    ratings: [],
    completionEstimateFast: "",
    completionEstimateLong: "",
    challengeEligibility: "eligible",
    availableDigital: false,
    availablePhysical: false,
    multiplayerType: "none",
    delistedStatus: "none",
    delistedReason: "",
    hasNoAchievements: false,
    isAchievementSlop: false,
    isPublished: false,
    adminNotes: "",
  });

  const toggleEnabled = (field) => (checked) => {
    setEnabled((prev) => ({ ...prev, [field]: !!checked }));
  };
  const updateValue = (field) => (v) => {
    setValues((prev) => ({ ...prev, [field]: v }));
  };
  const toggleInArray = (field, item) => {
    setValues((prev) => {
      const arr = prev[field] || [];
      const next = arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
      return { ...prev, [field]: next };
    });
  };

  const handleApply = async () => {
    const updates = {};
    for (const [k, on] of Object.entries(enabled)) {
      if (on) updates[k] = values[k];
    }
    if (Object.keys(updates).length === 0) {
      toast.error("Enable at least one field first");
      return;
    }
    try {
      setRunning(true);
      const res = await axios.post(`${API}/master/games/bulk-edit`, {
        game_ids: Array.from(selectedIds),
        updates,
      });
      toast.success(res.data.message);
      onOpenChange(false);
      setEnabled({});
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk edit failed");
    } finally {
      setRunning(false);
    }
  };

  // Section wrapper alias — points to the module-level BulkField.
  // eslint-disable-next-line react/no-unstable-nested-components
  const Field = (props) => (
    <BulkField {...props} enabled={enabled[props.name]} onToggle={toggleEnabled(props.name)} />
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !running && onOpenChange(o)}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">
            Bulk Edit — {selectedIds.size} game{selectedIds.size === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/60 text-xs mb-2">
          Check a field to include it. Only checked fields are applied — everything else is left untouched.
        </p>

        <div className="space-y-2">
          <Field name="publisher" label="Publisher">
            <Input value={values.publisher} onChange={(e) => updateValue("publisher")(e.target.value)} data-testid="bulk-edit-publisher" placeholder="e.g., Nintendo" />
          </Field>
          <Field name="developer" label="Developer">
            <Input value={values.developer} onChange={(e) => updateValue("developer")(e.target.value)} data-testid="bulk-edit-developer" placeholder="e.g., HAL Laboratory" />
          </Field>
          <Field name="releaseDate" label="Release Date">
            <Input type="date" value={values.releaseDate} onChange={(e) => updateValue("releaseDate")(e.target.value)} data-testid="bulk-edit-release-date" />
          </Field>
          <Field name="genres" label="Genres">
            <div className="grid grid-cols-3 gap-1">
              {(genresList.length ? genresList : ["Action", "Adventure", "RPG", "Puzzle", "Casual", "Shooter", "Sports", "Simulation", "Strategy", "Racing", "Fighting", "Platformer"]).map((g) => (
                <label key={g} className="flex items-center gap-2 text-xs text-white/70 hover:text-white cursor-pointer">
                  <Checkbox checked={values.genres.includes(g)} onCheckedChange={() => toggleInArray("genres", g)} />
                  {g}
                </label>
              ))}
            </div>
          </Field>
          <Field name="ratings" label="Ratings">
            <div className="flex flex-wrap gap-2">
              {RATINGS_LIST.map((r) => (
                <label key={r} className="flex items-center gap-2 text-xs text-white/70 hover:text-white cursor-pointer border border-white/10 rounded px-2 py-1">
                  <Checkbox checked={values.ratings.includes(r)} onCheckedChange={() => toggleInArray("ratings", r)} />
                  {r}
                </label>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field name="completionEstimateFast" label="Fast Completion">
              <Input value={values.completionEstimateFast} onChange={(e) => updateValue("completionEstimateFast")(e.target.value)} placeholder="e.g., 8-10 hrs" data-testid="bulk-edit-fast" />
            </Field>
            <Field name="completionEstimateLong" label="Full Completion">
              <Input value={values.completionEstimateLong} onChange={(e) => updateValue("completionEstimateLong")(e.target.value)} placeholder="e.g., 40-60 hrs" data-testid="bulk-edit-long" />
            </Field>
          </div>
          <Field name="challengeEligibility" label="Challenge Eligibility">
            <Select value={values.challengeEligibility} onValueChange={updateValue("challengeEligibility")}>
              <SelectTrigger data-testid="bulk-edit-challenge"><SelectValue /></SelectTrigger>
              <SelectContent>{CHALLENGE_LIST.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field name="availableDigital" label="Digital Available">
              <label className="flex items-center gap-2 text-xs text-white/70">
                <Checkbox checked={values.availableDigital} onCheckedChange={updateValue("availableDigital")} />
                Available Digital
              </label>
            </Field>
            <Field name="availablePhysical" label="Physical Available">
              <label className="flex items-center gap-2 text-xs text-white/70">
                <Checkbox checked={values.availablePhysical} onCheckedChange={updateValue("availablePhysical")} />
                Available Physical
              </label>
            </Field>
          </div>
          <Field name="multiplayerType" label="Multiplayer Type">
            <Select value={values.multiplayerType} onValueChange={updateValue("multiplayerType")}>
              <SelectTrigger data-testid="bulk-edit-multiplayer"><SelectValue /></SelectTrigger>
              <SelectContent>{MULTIPLAYER_LIST.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field name="delistedStatus" label="Delisted / Availability Status">
            <div className="space-y-2">
              <Select value={values.delistedStatus} onValueChange={updateValue("delistedStatus")}>
                <SelectTrigger data-testid="bulk-edit-delisted-status"><SelectValue /></SelectTrigger>
                <SelectContent>{DELISTED_LIST.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Reason (optional)" value={values.delistedReason} onChange={(e) => updateValue("delistedReason")(e.target.value)} data-testid="bulk-edit-delisted-reason" />
            </div>
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field name="hasNoAchievements" label="No Achievements">
              <label className="flex items-center gap-2 text-xs text-white/70">
                <Checkbox checked={values.hasNoAchievements} onCheckedChange={updateValue("hasNoAchievements")} />
                No achievements
              </label>
            </Field>
            <Field name="isAchievementSlop" label="Achievement Slop">
              <label className="flex items-center gap-2 text-xs text-white/70">
                <Checkbox checked={values.isAchievementSlop} onCheckedChange={updateValue("isAchievementSlop")} />
                Mark as slop
              </label>
            </Field>
            <Field name="isPublished" label="Published (Browse)">
              <label className="flex items-center gap-2 text-xs text-white/70">
                <Checkbox checked={values.isPublished} onCheckedChange={updateValue("isPublished")} />
                Published
              </label>
            </Field>
          </div>
          <Field name="adminNotes" label="Admin Notes (private)">
            <Textarea
              value={values.adminNotes}
              onChange={(e) => updateValue("adminNotes")(e.target.value)}
              rows={3}
              placeholder="e.g. Servers shutting down 2026-03-01"
              data-testid="bulk-edit-admin-notes"
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={running} data-testid="bulk-edit-cancel">Cancel</Button>
          <Button
            className="bg-purple-500 hover:bg-purple-600 text-white"
            onClick={handleApply}
            disabled={running || Object.values(enabled).every((v) => !v)}
            data-testid="bulk-edit-apply"
          >
            {running ? "Applying…" : `Apply to ${selectedIds.size} game${selectedIds.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkEditDialog;
